import { Workbook } from 'exceljs';
import type { TFunction } from 'i18next';
import type { ObjectPaymentReportRow } from '@econtractor/types';
import { DateTime } from 'luxon';
import { objectPaymentName, paymentCurrencyTotals, paymentColumns, paymentDate, paymentUserName, type PaymentReportGroup, type PaymentReportState, type PaymentSortKey } from './reportObjectPayments';

interface ExportPaymentReportParams {
    rows: ObjectPaymentReportRow[];
    groups: PaymentReportGroup[];
    filters: PaymentReportState;
    sortKey: PaymentSortKey;
    direction: 'asc' | 'desc';
    t: TFunction;
}

export const exportObjectPaymentsReport = async ({ rows, groups, filters, sortKey, direction, t }: ExportPaymentReportParams): Promise<void> => {
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet(t('objectPayments.title').slice(0, 31));
    [42, 26, 26, 24, 22, 42].forEach((width, index) => { sheet.getColumn(index + 1).width = width; });
    const heading = (text: string, bold = false) => {
        const row = sheet.addRow([text]);
        sheet.mergeCells(row.number, 1, row.number, 6);
        row.font = { bold };
        row.alignment = { wrapText: true };
        return row;
    };
    heading(t('objectPayments.title'), true);
    heading(`${t(`dateFilter.${filters.dateFilter.period}`)}: ${(filters.dateFilter.dates || []).map((date) => DateTime.fromMillis(date).toFormat('dd.MM.yyyy')).join(' — ')}`);
    const typeLabels = [
        filters.includeObjects ? t('reportFilter.objects') : '',
        filters.includeSales ? t('reportFilter.sales') : '',
        filters.includeServiceSales ? t('reportFilter.serviceSales') : '',
    ].filter(Boolean);
    heading(`${typeLabels.join(', ')}; ${t(filters.isVat === null ? 'reportFilter.all' : filters.isVat ? 'reportFilter.withVat' : 'reportFilter.noVat')}`);
    heading(filters.detail ? t(`objectPayments.group.${filters.groupBy}`) : `${t('objectPayments.summary')}; ${sortKey === 'ID' ? 'ID' : t(`objectPayments.columns.${sortKey}`)} ${direction === 'asc' ? '↑' : '↓'}`);
    sheet.addRow([]);
    const header = () => {
        const row = sheet.addRow(paymentColumns.map((key) => t(`objectPayments.columns.${key}`)));
        row.font = { bold: true };
        row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
        return row.number;
    };
    const total = (list: ObjectPaymentReportRow[], label: string) => {
        const writeTotal = (deleted: boolean, title: string) => {
            const totals = paymentCurrencyTotals(list, deleted);
            [0, 1].forEach((usd) => {
                const value = usd ? totals.usd : totals.uah;
                const row = sheet.addRow([title, '', '', '', Math.round(value * 100) / 100, '']);
                row.font = deleted ? { bold: true, color: { argb: 'FFFF0000' } } : { bold: true };
                row.getCell(5).numFmt = `${usd ? '"$"' : '"₴"'} #,##0.00;[Red]-${usd ? '"$"' : '"₴"'} #,##0.00`;
            });
        };
        writeTotal(false, label);
        if (list.some((row) => !!Number(row.documentDeleted))) writeTotal(true, t('objectPayments.deletedDocumentsTotal'));
    };
    const writeRows = (list: ObjectPaymentReportRow[]) => list.forEach((payment) => {
        const row = sheet.addRow([
            `${objectPaymentName(payment)}${payment.clientName ? ` / ${payment.clientName}` : ''}${Number(payment.objectDeleted) ? ` (${t('objectPayments.deletedObject')})` : ''}`,
            paymentUserName(payment.ownerName, payment.ownerId),
            paymentUserName(payment.authorName, payment.authorId),
            paymentDate(payment.dt),
            Number(payment.payment),
            `${payment.documentName || `#${payment.generationId}`}${Number(payment.documentDeleted) ? ` (${t('objectPayments.deletedDocument')})` : ''}`,
        ]);
        row.getCell(5).numFmt = `${Number(payment.paymentUsd) ? '"$"' : '"₴"'} #,##0.00;[Red]-${Number(payment.paymentUsd) ? '"$"' : '"₴"'} #,##0.00`;
        row.alignment = { vertical: 'top', wrapText: true };
    });
    if (filters.detail) {
        groups.forEach((group) => {
            heading(group.name, true);
            header();
            writeRows(group.rows);
            total(group.rows, t('objectPayments.groupTotal'));
            sheet.addRow([]);
        });
    } else {
        const headerIndex = header();
        writeRows(rows);
        sheet.autoFilter = { from: { row: headerIndex, column: 1 }, to: { row: headerIndex + rows.length, column: 6 } };
        sheet.views = [{ state: 'frozen', ySplit: headerIndex }];
    }
    total(rows, t('objectPayments.total'));
    const buffer = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `object-payments-${DateTime.now().toFormat('yyyyMMdd-HHmm')}.xlsx`;
    document.body.appendChild(link);
    try { link.click(); } finally { link.remove(); URL.revokeObjectURL(url); }
};
